import React, { createContext, useContext, useState, useEffect } from 'react';

interface DevelopmentContextType {
  isLocalDevelopment: boolean;
  isLocalComingSoonDisabled: boolean;
  toggleLocalComingSoon: () => void;
  shouldShowComingSoon: boolean;
}

const DevelopmentContext = createContext<DevelopmentContextType | undefined>(undefined);

export const useDevelopment = () => {
  const context = useContext(DevelopmentContext);
  if (context === undefined) {
    throw new Error('useDevelopment must be used within a DevelopmentProvider');
  }
  return context;
};

interface DevelopmentProviderProps {
  children: React.ReactNode;
}

export const DevelopmentProvider: React.FC<DevelopmentProviderProps> = ({ children }) => {
  const [isLocalDevelopment, setIsLocalDevelopment] = useState(false);
  const [isLocalComingSoonDisabled, setIsLocalComingSoonDisabled] = useState(false);

  useEffect(() => {
    // Check if we're running on localhost
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');
    
    setIsLocalDevelopment(isLocalhost);
    
    // Load local development state from localStorage
    const localState = localStorage.getItem('localComingSoonDisabled');
    if (localState) {
      setIsLocalComingSoonDisabled(JSON.parse(localState));
    }
  }, []);

  const toggleLocalComingSoon = () => {
    const newState = !isLocalComingSoonDisabled;
    setIsLocalComingSoonDisabled(newState);
    localStorage.setItem('localComingSoonDisabled', JSON.stringify(newState));
  };

  // Determine if we should show coming soon page
  // In development, we can override the Firebase setting
  const shouldShowComingSoon = isLocalDevelopment ? !isLocalComingSoonDisabled : true;

  const value = {
    isLocalDevelopment,
    isLocalComingSoonDisabled,
    toggleLocalComingSoon,
    shouldShowComingSoon
  };

  return (
    <DevelopmentContext.Provider value={value}>
      {children}
    </DevelopmentContext.Provider>
  );
};
